/**
 * The ids of FTUX - Getting Started (flow `brand-ftue`, script v1): every piece, step, control, action, outcome and
 * event type the flow reports to ows-uxo (momentum-online ows_uxo/docs/EVENTS.md), as const arrays with their union
 * types. It is the one list the UXO Center reuses: a timeline's lane node is the piece id, or a flow step's prefix
 * before ':'.
 *
 * Pure data (its one import, the studio's menu, is data too), so jest and every eager module can load it. Every id
 * matches the server's pattern ^[a-z0-9:-]{1,64}$. Beyond the tables of EVENTS.md the port also sends: the step
 * blog-on-switch, the skip:<reason> steps, 'finished' as a QA jump target, and every card, map and page control id.
 */
import { RAIL_DOORS, type RailDoorKey } from '../../layouts/MainLayout/studioMenu';

/** The flow id every event carries. */
export const FTUX_FLOW = 'brand-ftue';

/** The script's version: the events' `v` and the persisted record's `script`. */
export const FTUX_SCRIPT_VERSION = 1;

const isOneOf = <T extends string>(list: readonly T[], value: unknown): value is T =>
  typeof value === 'string' && (list as readonly string[]).includes(value);

/* Pieces */

/** The pieces in the script's order, the order the flow offers them. */
export const BRAND_PIECE_IDS = [
  'logo',
  'style',
  'banners',
  'games',
  'list',
  'blog-on',
  'blog-post',
  'blog-autopilot',
  'mail',
  'ad-banners',
  'ad-html5',
  'ad-videos',
  'ad-games',
  'affiliates',
] as const;
export type BrandPieceId = (typeof BRAND_PIECE_IDS)[number];

/** A known piece id (a persisted record may name one this build does not know). */
export const isBrandPieceId = (value: unknown): value is BrandPieceId => isOneOf(BRAND_PIECE_IDS, value);

/** Marketing's ad areas (owners-hub's Marketing hub area ids, landing pages aside): one per ad piece. */
export const AD_AREAS = ['banner-kits', 'html5-banners', 'videos', 'playables'] as const;
export type AdArea = (typeof AD_AREAS)[number];
export type AdPieceId = Extract<BrandPieceId, `ad-${string}`>;

/** The area each ad piece plays in: its hub card, its library, its step ids (ads-<area>-ask|open|start). */
export const AD_AREA_BY_PIECE = {
  'ad-banners': 'banner-kits',
  'ad-html5': 'html5-banners',
  'ad-videos': 'videos',
  'ad-games': 'playables',
} as const satisfies Readonly<Record<AdPieceId, AdArea>>;

/* The menu */

/**
 * The studio's menu entries: the desktop rail's doors, read from the studio's menu itself (studioMenu.ts), so the map's
 * parts and the rail's light follow it. The map's parts list them, the rail lights them.
 */
export const NAV_IDS: readonly RailDoorKey[] = RAIL_DOORS;
export type NavId = RailDoorKey;

export const isNavId = (value: unknown): value is NavId => isOneOf(NAV_IDS, value);

/**
 * The menu map's parts: the studio menu's four groups, in the order a rail tells them, and a phone's main menu (its
 * bottom bar), which a phone tells in Your brand's place.
 */
export const MAP_PARTS = ['numbers', 'grow', 'site', 'account', 'bar'] as const;
export type MapPart = (typeof MAP_PARTS)[number];

/* Steps */

/** Why the flow passed a piece by without the owner (its availability is never): the step of its `ended skipped`. */
export const SKIP_REASONS = ['logo-missing', 'logo-locked', 'logo-updating', 'blog-already-on', 'unavailable'] as const;
export type SkipReason = (typeof SKIP_REASONS)[number];

/** The flow's own steps, outside the pieces' views. */
export const FLOW_STEPS = [
  /** Lumio's hello, the start of a run. */
  'lumio-hello',
  /** The menu's map, one step per part as Lumio tells it. */
  'menu-map:numbers',
  'menu-map:grow',
  'menu-map:site',
  'menu-map:account',
  'menu-map:bar',
  /** A piece's card, Lumio's pick on Home's Spotlight. */
  'spotlight-pick',
  /** The piece's pop-up, opened from its card. */
  'piece-intro',
  /** Lumio's acknowledgment once a piece is in. */
  'ack',
  /** The end of the flow, as a QA jump target. */
  'finished',
  /** A piece passed by (ended skipped), by its reason. */
  'skip:logo-missing',
  'skip:logo-locked',
  'skip:logo-updating',
  'skip:blog-already-on',
  'skip:unavailable',
] as const;
export type FtuxFlowStep = (typeof FLOW_STEPS)[number];

/** The step of a map part as Lumio tells it. */
export const mapStep = (part: MapPart): FtuxFlowStep => `menu-map:${part}`;

/** The step a skipped piece's `ended` carries. */
export const skipStep = (reason: SkipReason): FtuxFlowStep => `skip:${reason}`;

/** Each piece's steps: the keys of the views Lumio's card shows inside the piece, in flow order. */
export const PIECE_STEPS = {
  logo: ['logo-ask', 'logo-open', 'logo-replace'],
  style: ['style-hub', 'style-open', 'style-new', 'style-ingredients'],
  banners: ['banners-ask', 'banners-open', 'banners-edit'],
  games: ['games-ask', 'games-move', 'games-publish'],
  list: ['list-show'],
  'blog-on': ['blog-on-hub', 'blog-on-open', 'blog-on-switch', 'blog-on-switching', 'blog-on-live'],
  'blog-post': ['blog-post-new'],
  'blog-autopilot': ['blog-autopilot-show', 'blog-autopilot-running'],
  mail: ['mail-find', 'mail-show', 'mail-made'],
  'ad-banners': ['ads-banner-kits-ask', 'ads-banner-kits-open', 'ads-banner-kits-start'],
  'ad-html5': ['ads-html5-banners-ask', 'ads-html5-banners-open', 'ads-html5-banners-start'],
  'ad-videos': ['ads-videos-ask', 'ads-videos-open', 'ads-videos-start'],
  'ad-games': ['ads-playables-ask', 'ads-playables-open', 'ads-playables-start'],
  affiliates: ['aff-find', 'aff-show'],
} as const satisfies Readonly<Record<BrandPieceId, readonly string[]>>;

/** The view keys of one piece. */
export type PieceStepOf<P extends BrandPieceId> = (typeof PIECE_STEPS)[P][number];
export type FtuxPieceStep = PieceStepOf<BrandPieceId>;
/** An event's step: a flow step, or the view key of the piece in play. */
export type FtuxStep = FtuxFlowStep | FtuxPieceStep;

/* Controls */

/**
 * The flow's own controls. Spotlight's pick card: cta, dismiss. The intro pop-up: cta, not-now (its close button and
 * Escape send not-now too). Lumio's card: back, escape, its answers by what they say, and the ack's dismiss; while a
 * piece plays, the close (its X) in the screen's corner sends dismiss (the piece waits on Spotlight). The menu's map: map-label (a part's label), map-skip, map-next (Next, on
 * every step but the last: Lumio goes on with the next part, or a phone's bar opens the drawer), got-it (the last step's), map-close (its close button
 * in the screen's corner: the map ends as after Got it, its parts not told yet skipped; Escape and a tap anywhere skip
 * as map-skip does), map-more (a phone's
 * More, opening the drawer whose parts Lumio then tells) and map-menu (any other door of the menu, the rail's or a
 * phone's, which ends the map and goes where it leads). The QA strip: jump and restart (its `qa` events take these two
 * only).
 */
export const FTUX_FLOW_CONTROLS = [
  'cta',
  'dismiss',
  'not-now',
  'back',
  'escape',
  'yes-show-me',
  'no-keep-it',
  'got-it',
  'replace-later',
  'create-later',
  'decide-later',
  'review-later',
  'personalize-later',
  'arrange-later',
  'turn-on-later',
  'post-later',
  'invite-later',
  'map-label',
  'map-skip',
  'map-next',
  'map-close',
  'map-more',
  'map-menu',
  'jump',
  'restart',
] as const;

/**
 * The page controls a piece lights, by their FTUX target id (targets.ts): each zone that takes the owner's click
 * through the hold, and the control lit inside it. A click there is a `clicked` whose control is this id and whose
 * label is the English name the view gives it ('Logos', 'Replace logo').
 */
export const FTUX_PAGE_CONTROLS = [
  // Website hub cards: Logos, Art Style, Website Creatives, Blog.
  'hub-logos',
  'hub-art-style',
  'hub-creatives',
  'hub-blog',
  // Logos: the primary logo's row and its Replace logo.
  'logo-primary',
  'logo-replace',
  // Art Style: the strip's New style card and its plus.
  'style-new',
  'style-new-plus',
  // Website Creatives: the pool's first banner card and its Edit.
  'banners-first-card',
  'banners-first-edit',
  // Game Lists: the open list's games (and its first five, what players meet first), its Publish this list, the page's
  // Add list.
  'games-list',
  'games-list-top',
  'games-publish-list',
  'games-add-list',
  // Blog: the switch with its state word, the switch, New article, the Autopilot panel and its switch.
  'blog-toggle',
  'blog-switch',
  'blog-new-article',
  'blog-autopilot',
  'blog-autopilot-switch',
  // Settings: the Mailbox row. Mailbox: its panel and Create free mailbox.
  'mail-row',
  'mail-panel',
  'mail-create-free',
  // Marketing hub cards, then each library's Create new tile.
  'hub-banner-kits',
  'hub-html5-banners',
  'hub-videos',
  'hub-playables',
  'ads-new-banner-kits',
  'ads-new-html5-banners',
  'ads-new-videos',
  'ads-new-playables',
  // Acquisition: the Affiliates card. Affiliates: the Affiliates tab and its Invite affiliate.
  'acq-affiliates',
  'aff-area',
  'aff-invite',
] as const;
export type FtuxPageControlId = (typeof FTUX_PAGE_CONTROLS)[number];

/** A page control id, for the zone of a click let through the hold. */
export const isFtuxPageControl = (value: unknown): value is FtuxPageControlId => isOneOf(FTUX_PAGE_CONTROLS, value);

/** Every control a `clicked` or `qa` event names, by an id that stays when its copy changes. */
export const FTUX_CONTROLS = [...FTUX_FLOW_CONTROLS, ...FTUX_PAGE_CONTROLS] as const;
export type FtuxControlId = (typeof FTUX_CONTROLS)[number];
/** The QA strip's controls, the only ones a `qa` event takes. */
export type FtuxQaControl = Extract<FtuxControlId, 'jump' | 'restart'>;

/* Actions, outcomes, event types */

/** What the owner did in the product itself, as a page signals it (ftuxSignal) or a piece reads it from the path. */
export const FTUX_ACTIONS = [
  'logo-replace-pressed',
  'style-ingredients-opened',
  'style-ingredients-closed',
  'banner-editor-opened',
  'games-moved',
  'games-published',
  'games-list-editor-opened',
  'blog-switch-pressed',
  'blog-editor-opened',
  'blog-autopilot-confirm-opened',
  'mail-create-opened',
  'ads-new-pressed',
  'affiliates-invite-opened',
] as const;
export type FtuxAction = (typeof FTUX_ACTIONS)[number];

/** How a piece ended: kept, made the owner's own, left for later, or passed by without an answer. */
export const FTUX_OUTCOMES = ['kept', 'changed', 'later', 'skipped'] as const;
export type FtuxOutcome = (typeof FTUX_OUTCOMES)[number];

/**
 * What an event reports. `missing`: something of the studio a step looks for was not on the page (a zone, a menu door,
 * the rail), so the tour went on without it; the team sees what the studio changed under the FTUX.
 */
export const FTUX_EVENT_TYPES = ['shown', 'clicked', 'did', 'ended', 'finished', 'qa', 'missing'] as const;
export type FtuxEventType = (typeof FTUX_EVENT_TYPES)[number];

/* Motion pictures */

/** The motion pictures: each piece intro's, then the ones on Lumio's card (scenes/registry.ts maps each to its module). */
export const FTUX_SCENE_KEYS = [
  'logo-start',
  'style-start',
  'banners-start',
  'games-start',
  'list-start',
  'blog-live-start',
  'blog-start',
  'blog-autopilot-start',
  'mail-start',
  'ad-banners-start',
  'ad-html5-start',
  'ad-videos-start',
  'ad-games-start',
  'affiliates-start',
  'blog-publish',
  'ad-banners-subjects',
  'ad-html5-subjects',
  'ad-videos-subjects',
  'ad-games-subjects',
] as const;
export type FtuxSceneKey = (typeof FTUX_SCENE_KEYS)[number];
